import { Crown, Diamond, Award, Trophy, Sparkles, type LucideIcon } from 'lucide-react';

// F5-R8c (dibujo «Progreso del agente», 2026-08-29) — las dos firmas del
// dibujo: TIER_THRESHOLDS (300/550/650/800) y STREAK_DAILY_TARGET (25)
// SALIERON del código. Eran la última copia compilada de la escalera vieja
// en web/ (el server ya había matado las suyas en common/pay-period.ts,
// arco escalera-F4) y chocaban con Configuración (500/700/900/1200 y meta
// 30). Verificado con grep antes de borrar: ninguna de las dos tenía UN
// solo lector — todo número de nivel y de meta ya viene del server
// (profile-summary trae `thresholds` anclados y `targetToday` resueltos
// de system_config, override individual incluido). Si algo necesita un
// umbral o la meta, se lee de la respuesta del server; acá no se
// re-declara nunca.

export type TierName = 'below_floor' | 'base' | 't1' | 't2' | 't3';

export const TIER_RANK: Record<TierName, number> = {
  below_floor: 0,
  base:        1,
  t1:          2,
  t2:          3,
  t3:          4,
};

export interface TierMeta {
  i18nLabelKey: string;
  icon: LucideIcon;
  ringClass: string;
  bgClass: string;
  textClass: string;
  gradientClass: string;
  emoji: string;
}

export const TIER_META: Record<TierName, TierMeta> = {
  below_floor: {
    i18nLabelKey: 'tier.caminoBronce',
    icon: Sparkles,
    ringClass: 'ring-slate-300',
    bgClass:   'bg-slate-50',
    textClass: 'text-slate-700',
    gradientClass: 'from-slate-100 to-slate-200',
    emoji: '·',
  },
  base: {
    i18nLabelKey: 'tier.bronce',
    icon: Award,
    ringClass: 'ring-amber-400',
    bgClass:   'bg-amber-50',
    textClass: 'text-amber-900',
    gradientClass: 'from-amber-100 to-orange-200',
    emoji: 'B',
  },
  t1: {
    i18nLabelKey: 'tier.plata',
    icon: Trophy,
    ringClass: 'ring-slate-400',
    bgClass:   'bg-slate-100',
    textClass: 'text-slate-800',
    gradientClass: 'from-slate-200 to-slate-300',
    emoji: 'P',
  },
  t2: {
    i18nLabelKey: 'tier.oro',
    icon: Crown,
    ringClass: 'ring-yellow-400',
    bgClass:   'bg-yellow-50',
    textClass: 'text-yellow-900',
    gradientClass: 'from-yellow-100 to-amber-300',
    emoji: 'O',
  },
  t3: {
    i18nLabelKey: 'tier.diamante',
    icon: Diamond,
    ringClass: 'ring-cyan-400',
    bgClass:   'bg-cyan-50',
    textClass: 'text-cyan-900',
    gradientClass: 'from-cyan-100 to-sky-300',
    emoji: 'D',
  },
};

// STREAK_DAILY_TARGET murió acá (firma F5-R8c, ver cabecera): la racha lee
// la meta viva del server (`streak.target` / `today.targetToday`).
export const STREAK_SHOW_FLAME_MIN = 3;
export const STREAK_DIAMANTE_DE_CONSTANCIA_MIN = 10;

export type ProgressSummary = {
  agentId: number;
  displayName: string;
  today: {
    nyDate: string;
    screeningsToday: number;
    targetToday: number;
    hitTarget: boolean;
  };
  monthly: {
    periodYear: number;
    periodMonth: number;
    /**
     * A6 · #138 — inicio y fin (inclusivo) del ciclo de pago, fechas NY
     * YYYY-MM-DD. Opcionales: un API anterior no los manda.
     */
    periodStart?: string | null;
    periodEnd?: string | null;
    screeningsMonth: number;
    primaryScreeningsMonth: number;
    subScreeningsMonth: number;
    tierAchievedThisMonth: TierName;
    tierMaintained: TierName;
    tierDisplayed: TierName;
    nextTier: TierName | null;
    nextTierThreshold: number | null;
    screeningsToNextTier: number | null;
    progressPctToNextTier: number;
  };
  streak: {
    daysWorking: number;
    target: number;
    showFlame: boolean;
    streakBadgeEarned: boolean;
  };
  achievements: {
    diamanteDeConstancia: boolean;
    monthsAtBronceOrAbove: number;
    bestMonth: { periodYear: number; periodMonth: number; totalScreenings: number } | null;
  };
  last7Days: Array<{
    nyDate: string;
    screenings: number;
    hitTarget: boolean;
    isWorkingDay: boolean;
  }>;
  history12: Array<{
    periodYear: number;
    periodMonth: number;
    totalScreenings: number;
    tierReached: TierName;
  }>;
  pendingValidation: {
    todayCount: number;
    monthCount: number;
  };
  /** F5-R8c — ADITIVO: la escalera ANCLADA con la que el server clasificó el
   *  tier de este período (una sola fuente; el cliente no reclasifica con el
   *  valor vivo de system_config). Opcional para tolerar un API anterior. */
  thresholds?: TierThresholdsShape;
};

// ===== F5-R8c — deltas aditivos del endpoint admin =====

export type TierThresholdsShape = {
  base: number;
  t1: number;
  t2: number;
  t3: number;
};

/** Serie del panel «Progreso del agente» (solo el endpoint admin la trae). */
export type ProgressSeries = {
  weekly12: Array<{ weekStartNy: string; screenings: number }>;
  monthly12: Array<{ periodYear: number; periodMonth: number; screenings: number }>;
  yearly3: Array<{ periodYear: number; screenings: number }>;
};

/** GET /admin/agents/:id/profile-summary = ProgressSummary + serie + días
 *  activos del período. Los campos viejos no cambian (forma aditiva). */
export type AdminProgressSummary = ProgressSummary & {
  series?: ProgressSeries;
  activeDaysMonth?: number;
};
